import { Controller } from '@hotwired/stimulus'
import {
  ASYNC_RENDER_CHANNEL,
  PERIOD_SET_ID_PARAM,
  REPORTS_SETTING_KEY,
  RENDER_PARAM,
  SELECTED_DIMENSIONS_PARAM,
} from '@app/application/report/constants'
import {
  setQueryParams,
  getQueryParam,
  setQueryParam,
  getQueryParamsByPrefix,
} from '@app/utils/url-helpers'
import { getSafeJSONValue, setSafeJSONValue } from '@app/utils/local-storage'
import Polling from '@app/utils/polling'
import { SfLog } from '@app/adapters/logger'

type ReportSettings = {
  periodSetId?: string
  selectedDimensions?: Record<string, string>
  expanded?: boolean
  collapsed?: boolean
}

type RenderNotification = {
  report_id: number
  digest: string
  status: 'ready' | 'error'
}

// Backstop for a render job that dies before publishing — after this the user
// gets the error state with a retry button rather than an endless spinner.
const RENDER_TIMEOUT_MS = 3 * 60 * 1000

function formatSelectedDimensionsParams(
  selectedDimensions: Record<string, string>
): Record<string, string> {
  const params: Record<string, string> = {}
  Object.keys(selectedDimensions).forEach((dimension) => {
    params[`${SELECTED_DIMENSIONS_PARAM}[${dimension}]`] =
      selectedDimensions[dimension]
  })
  return params
}

export default class extends Controller<HTMLElement> {
  static targets = [
    'spinner',
    'error',
    'calculating',
    'calculatingPending',
    'calculatingError',
  ]
  static values = {
    tablePath: String,
    reportTemplateId: String,
    periodSetId: String,
    selectedDimensions: Object,
  }

  declare readonly spinnerTarget: HTMLElement
  declare readonly hasSpinnerTarget: boolean
  declare readonly errorTarget: HTMLElement
  declare readonly hasErrorTarget: boolean
  declare readonly calculatingPendingTarget: HTMLElement
  declare readonly hasCalculatingPendingTarget: boolean
  declare readonly calculatingErrorTarget: HTMLElement
  declare readonly hasCalculatingErrorTarget: boolean
  declare tablePathValue: string
  declare reportTemplateIdValue: string
  declare periodSetIdValue: string
  declare selectedDimensionsValue: Record<string, string>

  private polling?: Polling<(data: RenderNotification) => void>
  private renderTimeout?: ReturnType<typeof setTimeout>
  private awaitedDigest = ''
  private awaitedReportId = ''

  connect(): void {
    this.element.addEventListener('turbo:frame-missing', this.handleError)
    this.element.addEventListener('turbo:fetch-request-error', this.handleError)

    this.applyLocalStorageSettings()
    this.loadFrame()
  }

  disconnect(): void {
    this.element.removeEventListener('turbo:frame-missing', this.handleError)
    this.element.removeEventListener(
      'turbo:fetch-request-error',
      this.handleError
    )
    this.stopWaitingForRender()
  }

  // Fires when the frame loads the "calculating" partial: subscribe to the
  // render channel from the captured message id and wait for this report's
  // notification (matched by digest), with a timeout backstop.
  calculatingTargetConnected(element: HTMLElement): void {
    this.awaitedDigest = element.dataset.digest ?? ''
    this.awaitedReportId = element.dataset.reportId ?? ''
    const lastId = Number(element.dataset.lastId ?? -1)

    this.polling = new Polling(
      ASYNC_RENDER_CHANNEL,
      lastId,
      this.handleRenderNotification
    )
    this.polling.subscribe()

    this.renderTimeout = setTimeout(
      () => this.showCalculatingError(),
      RENDER_TIMEOUT_MS
    )
  }

  calculatingTargetDisconnected(): void {
    this.stopWaitingForRender()
  }

  retry(): void {
    this.stopWaitingForRender()
    if (this.hasSpinnerTarget) {
      this.showSpinner()
    }
    this.loadFrame()
  }

  private handleRenderNotification = (data: RenderNotification): void => {
    if (!data || data.digest !== this.awaitedDigest) {
      return
    }
    if (
      this.awaitedReportId &&
      String(data.report_id) !== this.awaitedReportId
    ) {
      return
    }

    this.stopWaitingForRender()

    if (data.status === 'ready') {
      // The matrix is cached now, so reloading the frame renders it inline.
      this.loadFrame()
    } else {
      this.showCalculatingError()
    }
  }

  private stopWaitingForRender(): void {
    this.polling?.unsubscribe()
    this.polling = undefined
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout)
      this.renderTimeout = undefined
    }
  }

  private showCalculatingError(): void {
    this.stopWaitingForRender()
    if (this.hasCalculatingPendingTarget) {
      this.calculatingPendingTarget.classList.add('tw-hidden')
    }
    if (this.hasCalculatingErrorTarget) {
      this.calculatingErrorTarget.classList.remove('tw-hidden')
    }
  }

  private loadFrame(): void {
    this.element.setAttribute(
      'src',
      this.tablePathValue + window.location.search
    )
  }

  private handleError = (event: Event): void => {
    event.preventDefault()
    SfLog.warn('Failed to load report table frame', {
      extra: { type: event.type },
    })
    this.showError()
  }

  private showSpinner(): void {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('tw-hidden')
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove('tw-hidden')
    }
  }

  private showError(): void {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('tw-hidden')
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('tw-hidden')
    }
  }

  private applyLocalStorageSettings(): void {
    const reportId = this.reportTemplateIdValue
    const periodSetId = this.periodSetIdValue
    const selectedDimensions = this.selectedDimensionsValue

    const settings =
      (getSafeJSONValue(REPORTS_SETTING_KEY) as Record<string, unknown>) || {}
    const reportSettings =
      (getSafeJSONValue(
        `${REPORTS_SETTING_KEY}-${reportId}`
      ) as ReportSettings) || {}

    if (periodSetId) {
      if (getQueryParam(PERIOD_SET_ID_PARAM)) {
        reportSettings.periodSetId = periodSetId
        setSafeJSONValue(`${REPORTS_SETTING_KEY}-${reportId}`, reportSettings)
      } else if (reportSettings.periodSetId) {
        setQueryParam(PERIOD_SET_ID_PARAM, reportSettings.periodSetId, {
          replace: true,
        })
      }
    }

    if (Object.keys(selectedDimensions).length) {
      const selectedDimensionsParams = getQueryParamsByPrefix(
        SELECTED_DIMENSIONS_PARAM
      )
      if (Object.keys(selectedDimensionsParams).length) {
        reportSettings.selectedDimensions = selectedDimensions
        setSafeJSONValue(`${REPORTS_SETTING_KEY}-${reportId}`, reportSettings)
      } else if (reportSettings.selectedDimensions) {
        setQueryParams(
          formatSelectedDimensionsParams(reportSettings.selectedDimensions),
          { replace: true }
        )
      }
    }

    const mergedParams: Record<string, unknown> = {
      ...settings,
      [RENDER_PARAM]: 1,
    }
    setQueryParams(mergedParams, { replace: true })
  }
}
